import { isEqual } from 'lodash';
import { actionTypes, conditionIndicesViewOptions, toggleButtonOptions, stateKeys, filterProperties, stateProperties } from './constants';
import { allAssetTypes } from 'helpers/constants';
import {
    setSelectedConditions,
    addNewPageToConditionIndicesOverviewList,
    setSelectedSubscriptionTypes,
    updateAssetName,
    updateGroupAssets,
    updateFilterProperty,
    getTabKey,
    resetInitialLoadDoneOnFilterChange,
    hierarchyDataFormatter
} from './helpers';


const initialState = {
    assets: {
        data: {},
        loadingStatus: null,
        pageNumber: 1,
        showThreshold: false,
        showTrend: false,
        hasMore: false,
        initialLoadDone: false
    },
    assetGroups: {
        data: [],
        loadingStatus: null,
        pageNumber: 1,
        showThreshold: false,
        showTrend: false,
        hasMore: false,
        initialLoadDone: false
    },
    powertrains: {
        data: [],
        loadingStatus: null,
        pageNumber: 1,
        showThreshold: false,
        showTrend: false,
        hasMore: false,
        initialLoadDone: false
    },
    hierarchy: {
        data: [],
        loadingStatus: null,
        initialLoadDone: false
    },
    subscriptionTypes: {
        data: [],
        loadingStatus: null
    },
    filter: {
        assets: {
            assetTypeFilter: allAssetTypes,
            conditionFilter: [],
            subscriptionTypeFilter: [],
            searchBox: '',
            sensorIdentifierFilter: ''
        },
        assetGroups: {
            assetTypeFilter: allAssetTypes,
            conditionFilter: [],
            subscriptionTypeFilter: [],
            searchBox: '',
            sensorIdentifierFilter: ''
        },
        powertrains: {
            assetTypeFilter: allAssetTypes,
            conditionFilter: [],
            subscriptionTypeFilter: [],
            searchBox: '',
            sensorIdentifierFilter: ''
        },
        hierarchy: {
            assetTypeFilter: allAssetTypes,
            conditionFilter: [],
            subscriptionTypeFilter: [],
            searchBox: '',
            sensorIdentifierFilter: ''
        }
    },
    selectedTab: toggleButtonOptions.POWERTRAINS,
    isFilterInProgress: false,
    previousSearchBoxFilter: '',
    prevSelectedFilters: null,
    selectedAsset: null,
    isNavigationSearch: false,
    savedScrollTop: null,
    conditionIndicesView: conditionIndicesViewOptions.LIST,
};

// Helper function to handle GET overview actions
const handleGetOverview = (state, action, key) => {
    const tabKey = getTabKey(state.selectedTab, state.conditionIndicesView);
    const hasSearchBoxChanged = state.previousSearchBoxFilter !== state[stateProperties.FILTER]?.[tabKey]?.[filterProperties.SEARCH_BOX];
    return {
        ...state,
        [key]: {
            ...addNewPageToConditionIndicesOverviewList(state[key], action)
        },
        isFilterInProgress: hasSearchBoxChanged
    };
};

// Helper function to handle UPDATE_SELECTED_FILTERS
const handleUpdateSelectedFilters = (state, action) => {
    const filtersChanged = state.prevSelectedFilters && !isEqual(state.prevSelectedFilters, action.selectedFilters);
    return {
        ...state,
        prevSelectedFilters: action.selectedFilters,
        isFilterInProgress: filtersChanged ? true : state.isFilterInProgress,
        [stateKeys.POWERTRAINS]: { ...state[stateKeys.POWERTRAINS], [stateProperties.INITIAL_LOAD_DONE]: filtersChanged ? false : state[stateKeys.POWERTRAINS]?.[stateProperties.INITIAL_LOAD_DONE] },
        [stateKeys.ASSET_GROUPS]: { ...state[stateKeys.ASSET_GROUPS], [stateProperties.INITIAL_LOAD_DONE]: filtersChanged ? false : state[stateKeys.ASSET_GROUPS]?.[stateProperties.INITIAL_LOAD_DONE] },
        [stateKeys.ASSETS]: { ...state[stateKeys.ASSETS], [stateProperties.INITIAL_LOAD_DONE]: filtersChanged ? false : state[stateKeys.ASSETS]?.[stateProperties.INITIAL_LOAD_DONE] },
        [stateKeys.HIERARCHY]: { ...state[stateKeys.HIERARCHY], [stateProperties.INITIAL_LOAD_DONE]: filtersChanged ? false : state[stateKeys.HIERARCHY]?.[stateProperties.INITIAL_LOAD_DONE] },
        [stateProperties.FILTER]: filtersChanged ? { ...initialState[stateProperties.FILTER] } : state[stateProperties.FILTER]
    };
};

const handleRefreshSelectedFilters = (state) => {
    return {
        ...state,
        prevSelectedFilters: null,
        [stateKeys.POWERTRAINS]: { ...initialState[stateKeys.POWERTRAINS] },
        [stateKeys.ASSET_GROUPS]: { ...initialState[stateKeys.ASSET_GROUPS] },
        [stateKeys.ASSETS]: { ...initialState[stateKeys.ASSETS] },
        [stateKeys.HIERARCHY]: { ...initialState[stateKeys.HIERARCHY] },
        [stateProperties.FILTER]: { ...initialState[stateProperties.FILTER] }
    };
};

// Helper function to handle asset name updates
const handleUpdateAssetName = (state, action) => ({
    ...state,
    [stateKeys.ASSETS]: { ...state[stateKeys.ASSETS], [stateProperties.DATA]: updateAssetName(state[stateKeys.ASSETS]?.[stateProperties.DATA], action.data) },
    [stateKeys.ASSET_GROUPS]: { ...state[stateKeys.ASSET_GROUPS], [stateProperties.DATA]: updateGroupAssets(state[stateKeys.ASSET_GROUPS]?.[stateProperties.DATA], action.data) },
    [stateKeys.POWERTRAINS]: { ...state[stateKeys.POWERTRAINS], [stateProperties.DATA]: updateGroupAssets(state[stateKeys.POWERTRAINS]?.[stateProperties.DATA], action.data) },
    [stateKeys.HIERARCHY]: { ...state[stateKeys.HIERARCHY], [stateProperties.DATA]: updateGroupAssets(state[stateKeys.HIERARCHY]?.[stateProperties.DATA], action.data) }
});

const updatePreviousSearchFilter = (state, action) => {
    const tabKey = getTabKey(state.selectedTab, state.conditionIndicesView);
    return {
        ...state,
        [stateProperties.FILTER]: {
            ...state[stateProperties.FILTER],
            [action.tabKey]: {
                ...state[stateProperties.FILTER][tabKey],
                [filterProperties.SEARCH_BOX]: action.searchParams
            }
        },
        previousSearchBoxFilter: action?.searchParams ? action?.searchParams : state[stateProperties.FILTER]?.[tabKey]?.[filterProperties.SEARCH_BOX],
    };
};

// Helper function to clear overview data
const handleClearOverview = (state, key) => ({
    ...state,
    [key]: initialState[key],
    [stateProperties.FILTER]: initialState[stateProperties.FILTER],
    isFilterInProgress: false
});

const actionHandlers = {
    [actionTypes.GET_ASSET_OVERVIEW]: (state, action) => handleGetOverview(state, action, stateKeys.ASSETS),
    [actionTypes.GET_ASSET_GROUP_OVERVIEW]: (state, action) => handleGetOverview(state, action, stateKeys.ASSET_GROUPS),
    [actionTypes.GET_POWERTRAIN_OVERVIEW]: (state, action) => handleGetOverview(state, action, stateKeys.POWERTRAINS),
    [actionTypes.SET_ACTIVE_TAB]: (state, action) => ({ ...state, selectedTab: action.tab, savedScrollTop: null, savedScrollTopByTab: {} }),
    [actionTypes.UPDATE_SELECTED_FILTERS]: (state, action) => handleUpdateSelectedFilters(state, action),
    [actionTypes.REFRESH_SELECTED_FILTERS]: (state) => handleRefreshSelectedFilters(state),
    [actionTypes.SET_ASSET_TYPES_FILTER]: (state, action) => {
        const assetTypeValue = action.assetTypeFilter === allAssetTypes ? null : action.assetTypeFilter;
        const { filter: updatedFilter, changed } = updateFilterProperty(state, filterProperties.ASSET_TYPE_FILTER, assetTypeValue);

        return {
            ...resetInitialLoadDoneOnFilterChange(state, changed),
            filter: updatedFilter
        };
    },
    [actionTypes.TOGGLE_CONDITION_STATUS_FILTER]: (state, action) => {
        const tabKey = getTabKey(state.selectedTab, state.conditionIndicesView);
        const conditionValue = setSelectedConditions(
            state.filter[tabKey][filterProperties.CONDITION_FILTER],
            action.condition
        );
        const { filter: updatedFilter, changed } = updateFilterProperty(state, filterProperties.CONDITION_FILTER, conditionValue);

        return {
            ...resetInitialLoadDoneOnFilterChange(state, changed),
            filter: updatedFilter
        };
    },
    [actionTypes.GET_SUBSCRIPTION_TYPES_SETTINGS]: (state, action) => ({
        ...state,
        [stateKeys.SUBSCRIPTION_TYPES]: {
            ...state[stateKeys.SUBSCRIPTION_TYPES],
            [stateProperties.DATA]: action?.data?.subscriptionTypes,
            [stateProperties.LOADING_STATUS]: action?.loadingStatus
        }
    }),
    [actionTypes.SET_SUBSCRIPTION_TYPE_FILTER]: (state, action) => {
        const subscriptionValue = setSelectedSubscriptionTypes(action.subscriptionTypeFilter);
        const { filter: updatedFilter, changed } = updateFilterProperty(state, filterProperties.SUBSCRIPTION_TYPE_FILTER, subscriptionValue);

        return {
            ...resetInitialLoadDoneOnFilterChange(state, changed),
            filter: updatedFilter
        };
    },
    [actionTypes.SET_SENSOR_IDENTIFIER_FILTER]: (state, action) => {
        const sensorIdentifierValue = action.sensorIdentifierFilter || '';
        const { filter: updatedFilter, changed } = updateFilterProperty(state, filterProperties.SENSOR_IDENTIFIER_FILTER, sensorIdentifierValue);

        return {
            ...resetInitialLoadDoneOnFilterChange(state, changed),
            filter: updatedFilter
        };
    },
    [actionTypes.RESET_FILTERS]: (state) => {
        const tabKey = getTabKey(state.selectedTab, state.conditionIndicesView);
        if (!tabKey) {
            return state;
        }

        const isListOrHierarchyKey = tabKey === stateKeys.ASSETS || tabKey === stateKeys.HIERARCHY;
        const otherKey = tabKey === stateKeys.ASSETS ? stateKeys.HIERARCHY : stateKeys.ASSETS;

        const updatedFilter = isListOrHierarchyKey
            ? {
                ...state[stateProperties.FILTER],
                [tabKey]: { ...initialState[stateProperties.FILTER][tabKey] },
                [otherKey]: { ...state[stateProperties.FILTER][otherKey], [filterProperties.SEARCH_BOX]: '' }
            }
            : {
                ...state[stateProperties.FILTER],
                [tabKey]: { ...initialState[stateProperties.FILTER][tabKey] }
            };

        return {
            ...resetInitialLoadDoneOnFilterChange(state, true),
            ...isListOrHierarchyKey ? { [otherKey]: { ...state[otherKey], [stateProperties.INITIAL_LOAD_DONE]: false } } : {},
            [stateProperties.FILTER]: updatedFilter
        };
    },
    [actionTypes.SET_SEARCHBOX_FILTER]: (state, action) => {
        const tabKey = getTabKey(state.selectedTab, state.conditionIndicesView);
        if (!tabKey) {
            return state;
        }

        const isListOrHierarchyKey = tabKey === stateKeys.ASSETS || tabKey === stateKeys.HIERARCHY;
        const updatedFilter = isListOrHierarchyKey
            ? {
                ...state[stateProperties.FILTER],
                [stateKeys.ASSETS]: { ...state[stateProperties.FILTER][stateKeys.ASSETS], [filterProperties.SEARCH_BOX]: action.searchboxFilter },
                [stateKeys.HIERARCHY]: { ...state[stateProperties.FILTER][stateKeys.HIERARCHY], [filterProperties.SEARCH_BOX]: action.searchboxFilter }
            }
            : {
                ...state[stateProperties.FILTER],
                [tabKey]: { ...state[stateProperties.FILTER][tabKey], [filterProperties.SEARCH_BOX]: action.searchboxFilter }
            };

        return {
            ...state,
            [stateProperties.FILTER]: updatedFilter,
            ...isListOrHierarchyKey
                ? {
                    [stateKeys.ASSETS]: { ...state[stateKeys.ASSETS], [stateProperties.INITIAL_LOAD_DONE]: false },
                    [stateKeys.HIERARCHY]: { ...state[stateKeys.HIERARCHY], [stateProperties.INITIAL_LOAD_DONE]: false }
                }
                : { [tabKey]: { ...state[tabKey], [stateProperties.INITIAL_LOAD_DONE]: false } },
            isFilterInProgress: !!action.searchboxFilter
        };
    },
    [actionTypes.UPDATE_ASSET_NAME]: (state, action) => handleUpdateAssetName(state, action),
    [actionTypes.PREVIOUS_SEARCH_FILTER]: (state, action) => updatePreviousSearchFilter(state, action),
    [actionTypes.CLEAR_ASSET_OVERVIEW]: (state) => handleClearOverview(state, stateKeys.ASSETS),
    [actionTypes.CLEAR_ASSET_GROUP_OVERVIEW]: (state) => handleClearOverview(state, stateKeys.ASSET_GROUPS),
    [actionTypes.CLEAR_POWERTRAIN_OVERVIEW]: (state) => handleClearOverview(state, stateKeys.POWERTRAINS),
    // Invalidate powertrains, hierarchy and assets caches together after a config change
    [actionTypes.SET_UPDATED_POWERTRAIN]: (state, action) => {
        if (action.isPowertrainUpdated !== true) {
            return state;
        }
        return {
            ...state,
            [stateKeys.POWERTRAINS]: { ...state[stateKeys.POWERTRAINS], [stateProperties.INITIAL_LOAD_DONE]: false },
            [stateKeys.HIERARCHY]: { ...state[stateKeys.HIERARCHY], [stateProperties.INITIAL_LOAD_DONE]: false },
            [stateKeys.ASSETS]: { ...state[stateKeys.ASSETS], [stateProperties.INITIAL_LOAD_DONE]: false }
        };
    },
    [actionTypes.CLEAR_STORE]: () => ({ ...initialState }),
    [actionTypes.SET_SELECTED_ASSET]: (state, action) => ({
        ...state,
        selectedAsset: action.data
    }),
    [actionTypes.SET_NAVIGATION_SEARCH]: (state, action) => ({
        ...state,
        isNavigationSearch: action.value
    }),
    [actionTypes.SAVE_ASSET_OVERVIEW_SCROLL_TOP]: (state) => state,
    [actionTypes.SET_CONDITION_INDICES_VIEW]: (state, action) => ({
        ...state,
        conditionIndicesView: action.view
    }),
    [actionTypes.GET_HIERARCHY_CONDITION_INDICES_SETTINGS]: (state, action) => ({
        ...state,
        ...action?.data?.conditionIndicesView === conditionIndicesViewOptions.HIERARCHY
            ? { [stateKeys.HIERARCHY]: hierarchyDataFormatter(state[stateKeys.HIERARCHY], action) }
            : { [stateKeys.ASSETS]: hierarchyDataFormatter(state[stateKeys.ASSETS], action) }
    })
};

export const reducer = (state = initialState, action) => {
    const handler = actionHandlers[action.type];
    if (!handler) {
        return state;
    }
    return handler(state, action);
};
